import React, { useEffect, useState } from 'react';
import { settings as settingsApi } from '@/utils/api';
import { Save, Check, Eye, EyeOff, Send } from 'lucide-react';
import { toast } from 'sonner';

export default function AgencyEmailPanel({ currentSettings, reload }) {
  const [form, setForm] = useState({
    smtp_host: '', smtp_port: 587, smtp_user: '', smtp_pass: '', smtp_from: '', smtp_from_name: '', smtp_use_tls: true,
    imap_enabled: false, imap_host: '', imap_port: 993, imap_user: '', imap_pass: '', imap_ssl: true, imap_folder: 'INBOX',
    public_url: '',
  });
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [showPw, setShowPw] = useState({});
  const [testing, setTesting] = useState('');

  useEffect(() => {
    // Prefill non-sensitive fields from current settings (passwords stay blank)
    setForm(f => ({
      ...f,
      smtp_host: currentSettings.smtp_host && !currentSettings.smtp_pass_set ? '' : (currentSettings.smtp_host?.includes('•') ? currentSettings.smtp_host : (currentSettings.smtp_host || '')),
      smtp_port: currentSettings.smtp_port || 587,
      smtp_user: currentSettings.smtp_user || '',
      smtp_from: currentSettings.smtp_from || '',
      smtp_from_name: currentSettings.smtp_from_name || '',
      smtp_use_tls: currentSettings.smtp_use_tls !== false,
      imap_enabled: !!currentSettings.imap_enabled,
      imap_host: currentSettings.imap_host || '',
      imap_port: currentSettings.imap_port || 993,
      imap_user: currentSettings.imap_user || '',
      imap_ssl: currentSettings.imap_ssl !== false,
      imap_folder: currentSettings.imap_folder || 'INBOX',
      public_url: currentSettings.public_url || window.location.origin,
      dunning_enabled: !!currentSettings.dunning_enabled,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSettings.smtp_pass_set, currentSettings.imap_pass_set]);

  const save = async () => {
    setSaving(true);
    try {
      // Only send passwords if typed (blank = keep current)
      const payload = { ...form };
      if (!payload.smtp_pass) delete payload.smtp_pass;
      if (!payload.imap_pass) delete payload.imap_pass;
      await settingsApi.update(payload);
      toast.success('Email settings saved');
      if (reload) reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const testSmtp = async () => {
    if (!testTo) return toast.error('Enter a recipient email for the test');
    setTesting('smtp');
    try {
      const r = await settingsApi.testSmtp(testTo, 'Krris OS — SMTP test');
      toast.success(`SMTP test sent (${r.data.message_id ? 'Message-ID received' : 'OK'})`);
    } catch (e) { toast.error(e.response?.data?.detail || 'SMTP test failed'); }
    finally { setTesting(''); }
  };
  const testImap = async () => {
    setTesting('imap');
    try {
      const r = await settingsApi.testImap();
      toast.success(`IMAP connected to ${r.data.folder} (${r.data.messages} messages)`);
    } catch (e) { toast.error(e.response?.data?.detail || 'IMAP test failed'); }
    finally { setTesting(''); }
  };

  const PwField = ({ name, label, placeholder }) => (
    <div>
      <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>{label}</label>
      {currentSettings[`${name}_set`] && (
        <p className="text-xs mb-1 flex items-center gap-1" style={{ color: '#3C8A5A' }}>
          <Check size={11} /> Saved. Leave blank to keep unchanged.
        </p>
      )}
      <div className="relative">
        <input
          className="input-agency text-sm pr-10 font-mono"
          type={showPw[name] ? 'text' : 'password'}
          placeholder={placeholder}
          value={form[name] || ''}
          onChange={e => setForm({ ...form, [name]: e.target.value })}
          data-testid={`${name.replace(/_/g, '-')}`}
        />
        <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setShowPw(s => ({ ...s, [name]: !s[name] }))}>
          {showPw[name] ? <EyeOff size={14} style={{ color: 'var(--muted)' }} /> : <Eye size={14} style={{ color: 'var(--muted)' }} />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="card-base p-4 space-y-4" data-testid="agency-email-panel">
      <div className="flex items-center gap-2 mb-1">
        <Send size={16} style={{ color: '#006080' }} />
        <h3 className="font-bold text-sm" style={{ color: 'var(--ink)' }}>Agency Email (SMTP + IMAP)</h3>
      </div>
      <p className="text-xs" style={{ color: 'var(--muted)' }}>
        Agency-level SMTP is the fallback when a brand has no SMTP set — and is used for password-reset emails. Enabling IMAP lets inbound client emails land in your Inbox.
      </p>
      <p className="text-xs p-2 rounded-btn flex gap-2 items-start" style={{ background: '#E6F1F4', color: '#006080' }}>
        <span className="font-bold flex-shrink-0">Gmail?</span>
        <span>
          Use <code className="font-mono">smtp.gmail.com</code> · port <code className="font-mono">465</code> (implicit TLS) <em>or</em> <code className="font-mono">587</code> (STARTTLS). You must paste an{' '}
          <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline font-semibold">App Password</a> as the password (2FA required), <strong>not</strong> your normal Google password.
        </span>
      </p>

      <details className="rounded-btn" style={{ border: '1px solid var(--border)' }} open>
        <summary className="px-3 py-2 cursor-pointer text-sm font-semibold flex items-center justify-between" style={{ color: 'var(--ink)' }}>
          <span>Outbound — SMTP</span>
          {currentSettings.smtp_pass_set && <span className="badge-status" style={{ background: '#3C8A5A15', color: '#3C8A5A' }}>Configured</span>}
        </summary>
        <div className="p-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>SMTP host</label>
              <input className="input-agency text-sm" placeholder="smtp.gmail.com" value={form.smtp_host} onChange={e => setForm({ ...form, smtp_host: e.target.value })} data-testid="smtp-host" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Port</label>
              <input className="input-agency text-sm" type="number" value={form.smtp_port} onChange={e => setForm({ ...form, smtp_port: parseInt(e.target.value || '587') })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Username</label>
              <input className="input-agency text-sm" placeholder="ops@yourdomain.com" value={form.smtp_user} onChange={e => setForm({ ...form, smtp_user: e.target.value })} data-testid="agency-smtp-user" />
            </div>
            <PwField name="smtp_pass" label="Password / App password" placeholder="••••••••" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>From email</label>
              <input className="input-agency text-sm" placeholder="ops@yourdomain.com" value={form.smtp_from} onChange={e => setForm({ ...form, smtp_from: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>From name</label>
              <input className="input-agency text-sm" placeholder="Krris OS" value={form.smtp_from_name} onChange={e => setForm({ ...form, smtp_from_name: e.target.value })} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink)' }}>
            <input type="checkbox" checked={form.smtp_use_tls} onChange={e => setForm({ ...form, smtp_use_tls: e.target.checked })} /> Use STARTTLS
          </label>
          <div className="flex gap-2 pt-2" style={{ borderTop: '1px solid var(--border)' }}>
            <input className="input-agency text-sm flex-1" type="email" placeholder="send-test-to@example.com" value={testTo} onChange={e => setTestTo(e.target.value)} data-testid="smtp-test-to" />
            <button onClick={testSmtp} disabled={testing === 'smtp' || !testTo} className="text-xs px-3 py-1.5 rounded-btn font-semibold whitespace-nowrap" style={{ background: '#E6F1F4', color: '#006080' }} data-testid="smtp-test-button">
              {testing === 'smtp' ? 'Sending…' : 'Send test email'}
            </button>
          </div>
        </div>
      </details>

      <details className="rounded-btn" style={{ border: '1px solid var(--border)' }}>
        <summary className="px-3 py-2 cursor-pointer text-sm font-semibold flex items-center justify-between" style={{ color: 'var(--ink)' }}>
          <span>Inbound — IMAP (optional)</span>
          {currentSettings.imap_pass_set && <span className="badge-status" style={{ background: '#3C8A5A15', color: '#3C8A5A' }}>Configured</span>}
        </summary>
        <div className="p-3 space-y-3">
          <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink)' }}>
            <input type="checkbox" checked={form.imap_enabled} onChange={e => setForm({ ...form, imap_enabled: e.target.checked })} data-testid="imap-enabled-toggle" /> Enable IMAP polling (checks inbox every 60s)
          </label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>IMAP host</label>
              <input className="input-agency text-sm" placeholder="imap.gmail.com" value={form.imap_host} onChange={e => setForm({ ...form, imap_host: e.target.value })} data-testid="imap-host" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Port</label>
              <input className="input-agency text-sm" type="number" value={form.imap_port} onChange={e => setForm({ ...form, imap_port: parseInt(e.target.value || '993') })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Username</label>
              <input className="input-agency text-sm" placeholder="ops@yourdomain.com" value={form.imap_user} onChange={e => setForm({ ...form, imap_user: e.target.value })} />
            </div>
            <PwField name="imap_pass" label="Password / App password" placeholder="••••••••" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Folder</label>
              <input className="input-agency text-sm" placeholder="INBOX" value={form.imap_folder} onChange={e => setForm({ ...form, imap_folder: e.target.value })} />
            </div>
            <label className="flex items-end pb-2 gap-2 text-sm" style={{ color: 'var(--ink)' }}>
              <input type="checkbox" checked={form.imap_ssl} onChange={e => setForm({ ...form, imap_ssl: e.target.checked })} /> Use SSL
            </label>
          </div>
          <button onClick={testImap} disabled={testing === 'imap'} className="text-xs px-3 py-1.5 rounded-btn font-semibold" style={{ background: '#E6F1F4', color: '#006080' }} data-testid="imap-test-button">
            {testing === 'imap' ? 'Connecting…' : 'Test IMAP connection'}
          </button>
        </div>
      </details>

      <details className="rounded-btn" style={{ border: '1px solid var(--border)' }}>
        <summary className="px-3 py-2 cursor-pointer text-sm font-semibold flex items-center justify-between" style={{ color: 'var(--ink)' }}>
          <span>Dunning — auto-remind overdue invoices</span>
          {currentSettings.dunning_enabled && <span className="badge-status" style={{ background: '#3C8A5A15', color: '#3C8A5A' }}>On</span>}
        </summary>
        <div className="p-3 space-y-2">
          <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink)' }}>
            <input
              type="checkbox"
              checked={!!form.dunning_enabled}
              onChange={e => setForm({ ...form, dunning_enabled: e.target.checked })}
              data-testid="dunning-enabled-toggle"
            />
            Send polite reminders at 3 / 7 / 14 / 30 days overdue
          </label>
          <p className="text-[11px]" style={{ color: 'var(--muted)' }}>
            Uses your agency (or brand) SMTP. Only sends once per threshold per invoice. You can also click "Send reminder" on any invoice to nudge immediately.
          </p>
        </div>
      </details>

      <div>
        <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Public URL (used in email links)</label>
        <input className="input-agency text-sm font-mono" placeholder="https://yourapp.example.com" value={form.public_url} onChange={e => setForm({ ...form, public_url: e.target.value })} />
        <p className="text-[10px] mt-1" style={{ color: 'var(--muted)' }}>Used in password-reset, wizard, and invoice emails. Defaults to the current origin.</p>
      </div>

      <button className="btn-primary text-sm flex items-center gap-1.5 px-3 py-1.5" onClick={save} disabled={saving} data-testid="save-agency-email">
        <Save size={12} /> {saving ? 'Saving…' : 'Save email settings'}
      </button>
    </div>
  );
}
